import type { ComponentType, ReactNode } from "react";
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { Measurement, Sex, UnitsPreference } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { PainMapFigure } from "../bodymap/PainMapPicker";
import { painSummary } from "../bodymap/painSummary";
import { makeGlanceCardStyles } from "./glanceCardStyles";
import { Eyebrow } from "../primitives/Eyebrow";
import { helpTextStyle } from "../primitives/helpTextStyle";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { E1RMTrendsPanel } from "../Progress/E1RMTrendsPanel";
import { ExerciseFrequencyPanel } from "../Progress/ExerciseFrequencyPanel";
import { IntensityHeatmapPanel } from "../Progress/IntensityHeatmapPanel";
import { MovementBalancePanel } from "../Progress/MovementBalancePanel";
import { MuscleCoverageCard } from "../Progress/MuscleCoverageCard";
import { PainVolumePanel } from "../Progress/PainVolumePanel";
import { RepsAtLoadTrendsPanel } from "../Progress/RepsAtLoadTrendsPanel";
import { makeTierOneStyles } from "../Progress/panelStyles";
import type { ProgressDeps } from "../Progress/types";
import { MeasurementTrends } from "../Settings/measurements/MeasurementTrends";

// HR-11 fetta 2: the Home widgets that start off. Each one fetches through
// its own panel or host hook, so a widget the athlete has not turned on is
// never mounted and costs nothing. The session results come from the host's
// ONE cache (ProgressDeps.resultsCache), the same one Progress reads.

const PANELS = {
  e1rmTrends: E1RMTrendsPanel,
  repsAtLoad: RepsAtLoadTrendsPanel,
  movementBalance: MovementBalancePanel,
  painVolume: PainVolumePanel,
  intensityHeatmap: IntensityHeatmapPanel,
  exerciseFrequency: ExerciseFrequencyPanel,
} satisfies Record<string, ComponentType<ProgressDeps>>;

type InsightWidgetId = keyof typeof PANELS | "coverage" | "painMap" | "measurements";

/** The nodes HomeView places for the off-by-default widgets. Without
 *  `progressDeps` (or `measurementsSlot`) the widget renders nothing. */
export function homeInsightWidgets({
  theme,
  locale,
  progressDeps,
  measurementsSlot,
  onOpenPainMap,
}: {
  theme: UiTheme;
  locale: string;
  progressDeps?: ProgressDeps;
  measurementsSlot?: ReactNode;
  /** HR-11: opens the pain map page (Settings → Pain map). Without it the
   *  card stays read-only and untappable, same as before. */
  onOpenPainMap?: () => void;
}): Record<InsightWidgetId, ReactNode> {
  const styles = makeStyles(theme);
  const d = progressDeps;
  const frame = (node: ReactNode) => <View style={styles.gutter}>{node}</View>;
  const panel = (id: keyof typeof PANELS) => {
    const Panel = PANELS[id];
    return d ? frame(<Panel {...d} />) : null;
  };
  return {
    coverage: d ? frame(<MuscleCoverageCard deps={d} locale={locale} />) : null,
    e1rmTrends: panel("e1rmTrends"),
    repsAtLoad: panel("repsAtLoad"),
    movementBalance: panel("movementBalance"),
    painVolume: panel("painVolume"),
    intensityHeatmap: panel("intensityHeatmap"),
    exerciseFrequency: panel("exerciseFrequency"),
    painMap: d
      ? frame(<PainMapCard deps={d} onOpenPainMap={onOpenPainMap} />)
      : null,
    measurements: measurementsSlot ?? null,
  };
}

// The pain map, read-only figure + legend (same as the picker draws), but
// the whole card is a button: a tap opens the editor that already exists
// (Settings → Pain map), HR-11's follow-up to the pain-map widget. No callback
// (a host that doesn't wire one) means the card stays exactly as it was, an
// inert display. The figure itself is hidden from assistive tech while
// inside the Pressable (hiddenFromScreenReader) so the button is the ONE
// stop a screen reader finds here, not two: its own accessibilityLabel
// carries the same spoken summary the figure would have said alone.
function PainMapCard({
  deps,
  onOpenPainMap,
}: {
  deps: ProgressDeps;
  onOpenPainMap?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const card = makeTierOneStyles(deps.theme);
  const glance = makeGlanceCardStyles(deps.theme);
  const styles = makeStyles(deps.theme);
  const marked = Object.keys(deps.painMap).length > 0;
  const title = t("bodymap.painTitle");
  const emptyText = t("mobile.home.painMapEmpty", { defaultValue: "No pain marked." });
  // painSummary already opens with the title (it's the SAME spoken text a
  // lone read-only figure would say), so the marked label uses it as-is; the
  // empty branch has no title of its own, so this card supplies it once.
  const label = marked ? painSummary(t, deps.painMap, i18n.language) : `${title}: ${emptyText}`;
  const hint = t("mobile.home.painMapOpenHint", {
    defaultValue: "Opens your pain map to edit.",
  });
  const figure = marked ? (
    <PainMapFigure
      theme={deps.theme}
      isDark={deps.isDark}
      figure={deps.figure}
      painMap={deps.painMap}
    />
  ) : (
    <Text style={styles.help}>{emptyText}</Text>
  );

  if (!onOpenPainMap) {
    return (
      <View style={card.card}>
        <Text style={card.cardTitle}>{title}</Text>
        {figure}
      </View>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={hint}
      onPress={onOpenPainMap}
      style={({ pressed }) => [
        card.card,
        styles.tappableCard,
        pressed && glance.pressed,
      ]}
    >
      <View style={glance.textCol}>
        <Text style={card.cardTitle}>{title}</Text>
        <View {...hiddenFromScreenReader()}>{figure}</View>
      </View>
      <Text style={glance.chevron}>›</Text>
    </Pressable>
  );
}

/** The measurement trends as a Home widget. The host mounts it with the data
 *  from its own measurements hook, so the list is fetched only while the
 *  widget is on. */
export function HomeMeasurementsWidget({
  theme,
  items,
  loading,
  sex,
  units,
  locale,
}: {
  theme: UiTheme;
  items: Measurement[];
  loading: boolean;
  sex: Sex | undefined;
  units: UnitsPreference;
  locale: string;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  return (
    <View style={styles.gutter}>
      <Eyebrow theme={theme} style={styles.title}>
        {t("mobile.body.measurementsTitle", { defaultValue: "Measurements" })}
      </Eyebrow>
      {loading && items.length === 0 ? (
        <ActivityIndicator color={theme.palette.ink2} />
      ) : (
        <MeasurementTrends
          items={items}
          sex={sex}
          units={units}
          locale={locale}
          theme={theme}
          nowMs={Date.now()}
        />
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    // The Home box gutter (makeHomeBoxStyle) without the box: these cards
    // draw their own.
    gutter: { marginHorizontal: s.s5, marginBottom: s.s4 },
    title: { marginBottom: s.s2 },
    help: helpTextStyle(theme),
    // HR-11: the tappable PainMapCard turns the plain column card into a
    // row (title+figure column, trailing chevron), same idiom as the other
    // glance cards' `glance.card`. The column itself reuses `glance.textCol`.
    tappableCard: { flexDirection: "row", alignItems: "center" },
  });
}
